import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useLocation } from "react-router-dom";
import { billingApiHooks } from "@/api/billingApi";

const stripePromise = loadStripe(
  "pk_test_51RKzu6QEBCZXZ5s0guBhQN1OXB8qB3vlYfuutLRc8nRCHYBGI4UttnSEEwj7MeVavHQrHuU6O6EkpBy8mC7h4Rs900XTMc2jQ5"
);

const AddNewCardForm: React.FC<{ onSuccess?: () => void }> = ({
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Initialize the mutation hook at component level
  const setupIntentsMutation = billingApiHooks.useSetupIntents();

  // Debug logging for component state
  React.useEffect(() => {
    console.log("🔧 AddNewCardForm initialized", {
      stripe: !!stripe,
      elements: !!elements,
      loading,
      errorMessage,
    });
  }, [stripe, elements, loading, errorMessage]);

  // Add error boundary for iframe issues
  React.useEffect(() => {
    const handleIframeError = (event: ErrorEvent) => {
      if (event.message && event.message.includes("MutationObserver")) {
        console.log(
          "🛡️ Browser extension iframe interference detected:",
          event.message
        );
        // Don't show this error to the user as it's from browser extensions
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener("error", handleIframeError);

    return () => {
      window.removeEventListener("error", handleIframeError);
    };
  }, []);

  const handleError = (error: any) => {
    console.log("🚨 Error occurred:", error);
    setLoading(false);

    // Provide more detailed error messages
    let errorMsg = "An unexpected error occurred";
    if (error?.message) {
      errorMsg = error.message;
    } else if (typeof error === "string") {
      errorMsg = error;
    } else if (error?.error?.message) {
      errorMsg = error.error.message;
    }

    setErrorMessage(errorMsg);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      console.log("❌ Stripe or Elements not available", {
        stripe: !!stripe,
        elements: !!elements,
      });
      return;
    }

    console.log("🚀 Starting payment method setup process");
    setLoading(true);
    setErrorMessage(null);

    try {
      console.log("📝 Submitting payment element...");
      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.log("❌ Element submit error:", submitError);
        handleError(submitError);
        return;
      }
      console.log("✅ Payment element submitted successfully");

      const access_token = localStorage.getItem("access_token");
      console.log("🔑 Access token available:", !!access_token);

      if (!access_token) {
        console.log("❌ No access token found");
        handleError(new Error("Authentication required. Please log in again."));
        return;
      }

      console.log("🌐 Calling setup intents API...");
      const res = await setupIntentsMutation.mutateAsync({
        client_secret: access_token,
      });
      console.log("✅ Setup intents API response:", res);

      const { client_secret: clientSecret } = res;
      console.log("🔐 Client secret received:", !!clientSecret);

      if (!clientSecret) {
        console.log("❌ No client secret in response");
        handleError(new Error("Failed to get client secret from server"));
        return;
      }

      // Build the current URL for return_url
      const currentUrl =
        window.location.origin + location.pathname + location.search;
      console.log("🔗 Return URL:", currentUrl);

      console.log("💳 Confirming setup with Stripe...");
      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: currentUrl,
        },
      });

      console.log("🔍 Stripe confirmSetup result:", { error });

      if (error) {
        console.log("❌ Stripe confirmSetup error:", error);
        handleError(error);
      } else {
        console.log("✅ Payment method setup completed successfully");
        setLoading(false);
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.log("💥 Unexpected error during payment setup:", error);
      handleError(error);
    }
  };

  return (
    <form onSubmit={handleSubmit} id="payment-form" className="space-y-6">
      <div className="min-h-[200px] relative">
        <PaymentElement
          options={{
            layout: "tabs",
          }}
        />
        {/* Add a protective overlay for iframe issues */}
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background: "transparent",
            border: "none",
            outline: "none",
          }}
          onError={(e) => {
            console.log("🛡️ Iframe protection triggered:", e);
          }}
        />
      </div>
      <button
        type="submit"
        disabled={!stripe || loading}
        className="w-full bg-[#78C7D1] hover:bg-[#4fa3b3] disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
        id="submit"
      >
        {loading ? "Processing..." : "Add Payment Method"}
      </button>
      {errorMessage && (
        <div
          id="error-message"
          className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
        >
          {errorMessage}
        </div>
      )}
    </form>
  );
};

const AddNewCardWithStripe: React.FC<{
  onSuccess?: () => void;
  currentCardCount?: number;
  maxCards?: number;
}> = ({ onSuccess, currentCardCount = 0, maxCards = 2 }) => {
  const options = {
    mode: "setup" as const,
    currency: "usd",
    appearance: {
      theme: "stripe" as const,
      variables: {
        colorPrimary: "#78C7D1",
        colorBackground: "#ffffff",
        colorText: "#1f2937",
        colorDanger: "#ef4444",
        fontFamily: "Inter, system-ui, sans-serif",
        spacingUnit: "4px",
        borderRadius: "8px",
      },
    },
    // these params should match the backend ("card" and "link")
    paymentMethodOrder: ["card"],
    paymentMethodTypes: ["card"],
    // Add iframe protection
    loader: "auto" as const,
    locale: "en" as const,
  };

  // Debug logging for Stripe initialization
  React.useEffect(() => {
    console.log("🎯 AddNewCardWithStripe initialized", {
      stripePromise: !!stripePromise,
      options,
      onSuccess: !!onSuccess,
      currentCardCount,
      maxCards,
    });
  }, [onSuccess, currentCardCount, maxCards]);

  // Check if card limit is reached
  const isCardLimitReached = currentCardCount >= maxCards;

  if (isCardLimitReached) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-bold">!</span>
          </div>
          <h3 className="text-yellow-800 font-semibold">Card Limit Reached</h3>
        </div>
        <p className="text-yellow-700 text-sm">
          You have reached the maximum limit of {maxCards} payment methods.
          Please delete an existing card before adding a new one.
        </p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <AddNewCardForm onSuccess={onSuccess} />
    </Elements>
  );
};

export default AddNewCardWithStripe;
